import {
  Card, CardContent, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ModalComponent from '../../ModalComponent';
import ColorPicker from '../../Pickers/ColorPicker/ColorPicker';
import { ColorPickerColors } from '../../../models/ColorPicker';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import config from '../../../config';
import BasicImageSettingsComponent from '../../Blocks/BackgroundSettingsModal/BasicImageSettingsComponent';

interface ImageModalProps {
  handleOnConfirm: (url: BlockComponentImage) => void
  isShowModal?: boolean
  onCloseModal?: () => void
  backgroundImage?: BlockComponentImage,
  defaultBackgroundColor?: string,
}

const BackGroundImageModal = (props: ImageModalProps) => {
  const {
    handleOnConfirm, onCloseModal, isShowModal, backgroundImage: backgroundImageProp, defaultBackgroundColor,
  } = props;

  const [type, setType] = useState(backgroundImageProp?.solidColor ? 'color' : 'none');
  const [backgroundImageLocal, setBackgroundImageLocal] = useState<BlockComponentImage>(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
  const [selectedImage, setSelectedImage] = useState<BlockComponentImage>(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
  const [defaultImage, setDefaultImage] = useState<BlockComponentImage>(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());

  const onPickColor = (color: string) => {
    setBackgroundImageLocal({ ...backgroundImageLocal, value: '', solidColor: color });
  };

  useEffect(() => {
    if (backgroundImageLocal?.value) {
      setType('image');
    } else if (backgroundImageLocal?.solidColor) {
      setType('color');
    } else {
      setType('none');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    if (type === 'none') {
      backgroundImageLocal.solidColor = '';
      backgroundImageLocal.value = '';
      setDefaultImage({ ...backgroundImageLocal, value: config.defaultImageUrl });
      setSelectedImage({ ...backgroundImageLocal, value: config.defaultImageUrl });
    } else if (type === 'color') {
      backgroundImageLocal.value = '';
      setDefaultImage({ ...backgroundImageLocal, value: config.defaultImageUrl });
      setSelectedImage({ ...backgroundImageLocal, value: config.defaultImageUrl });
    } else if (type === 'image') {
      backgroundImageLocal.solidColor = '';
      if (!backgroundImageLocal.value) {
        backgroundImageLocal.value = config.defaultImageUrl;
      }
      setDefaultImage({ ...backgroundImageLocal });
    }
    handleOnConfirm(backgroundImageLocal);
    onCloseModal();
  };

  const storeSelectedImage = (image: BlockComponentImage) => {
    let tmpImage: BlockComponentImage;
    if (type === 'image') {
      if (image.value) {
        tmpImage = { ...image, value: image.value };
      } else {
        tmpImage = { ...defaultImage, value: defaultImage.value };
      }
      setSelectedImage(tmpImage);
    }
  };

  const loadSelectedImage = (image: BlockComponentImage) => {
    let tmpImage: BlockComponentImage;
    if (!image.value) {
      tmpImage = { ...backgroundImageLocal, value: config.defaultImageUrl, settings: { ...backgroundImageLocal.settings, parallaxBool: false } };
    } else {
      tmpImage = { ...image, value: image.value, settings: { ...backgroundImageLocal.settings } };
    }
    setBackgroundImageLocal(tmpImage);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === 'image') {
      loadSelectedImage(selectedImage);
      backgroundImageLocal.solidColor = '';
    } else {
      storeSelectedImage(backgroundImageLocal);
      backgroundImageLocal.value = '';
    }

    setType(e.target.value);
  };

  const handleCloseModal = () => {
    if (backgroundImageProp?.value) {
      setType('image');
    } else if (backgroundImageProp?.solidColor) {
      setType('color');
    } else {
      setType('none');
    }

    setBackgroundImageLocal(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
    setSelectedImage(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
    setDefaultImage(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
    onCloseModal();
  };

  useEffect(() => {
    if (isShowModal) {
      if (backgroundImageProp?.value) {
        setType('image');
      } else if (backgroundImageProp?.solidColor) {
        setType('color');
      } else {
        setType('none');
      }

      setBackgroundImageLocal(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
      setSelectedImage(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
      setDefaultImage(backgroundImageProp ? { ...backgroundImageProp } : new BlockComponentImage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowModal]);

  return (
    <ModalComponent
      size="lg"
      title="Background Settings"
      confirmButtonText="Apply"
      onConfirm={handleSave}
      show={isShowModal}
      closeModal={handleCloseModal}
      centered
      animation={false}
      backdropClassName="z-index-2000"
      style={{ zIndex: 2001 }}
      scrollable
    >
      <Card>
        <CardContent>
          <Grid container mb={0} spacing={0}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className="text-bold" mb={2} color="#495057" />

              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="audio"
                  defaultValue=""
                  onChange={(e) => handleRadioChange(e)}
                  value={type}
                >
                  <FormControlLabel value="none" control={<Radio />} label="None" />
                  <FormControlLabel value="color" control={<Radio />} label="Solid Color" />
                  <FormControlLabel value="image" control={<Radio />} label="Background Image" />
                </RadioGroup>
              </FormControl>
              <hr className="mt-4" />
              {type === 'color'
                && (
                  <>
                    <Grid item>
                      <Typography variant="subtitle2" className="text-bold" color="#495057">
                        Color
                      </Typography>
                    </Grid>
                    <Grid
                      container
                      mt={2}
                      direction="column"
                      alignItems="start"
                      justifyContent="center"
                    >
                      <Grid item>
                        <ColorPicker
                          value={backgroundImageLocal.solidColor as ColorPickerColors}
                          hasColorText={false}
                          onPickColor={onPickColor}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

              {type === 'image'
                && (
                  <>
                    <BasicImageSettingsComponent
                      defaultImage={defaultImage}
                      image={backgroundImageLocal}
                      onChange={(newImage: BlockComponentImage) => {
                        const tmpImage = { ...newImage };
                        setBackgroundImageLocal(tmpImage);
                      }}
                      isEditing
                    />
                  </>
                )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </ModalComponent>
  );
};

export default BackGroundImageModal;
